import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VariantProps } from 'tailwind-variants';
import { showErrorToast, showSuccessToast } from '@Helpers';
import { cn, formatApiDateTime } from '@Utils';

import { sendCustomEvent, sendErrorEvent } from '@Analytics';
import { open_icon } from '@Assets';
import { Badge, badgeVariants, Column, ConfirmationModal, Table } from '@Components';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useMasters } from '@CustomHooks';
import { ReplacementConflictError } from '@CustomHooks/configuration/useMasters';
import { ClientMaster, ConfigurationFileStatus, ReplacementConflict, ReplacementPreview } from '@Models';
import ReplacementPreviewModal from './ReplacementPreviewModal';

interface StatusBadgeVariant extends VariantProps<typeof badgeVariants> {
  label: string;
}

const openFileInNewWindow = ({ link, source }: ClientMaster): void => {
  if (!link) return;
  sendCustomEvent('CONFIGURATION_FILE_OPENED', { title: link.split('/').pop(), connector: source });
  window.open(link, '_blank');
};

/** The upload a person is partway through approving. */
interface PendingReplacement {
  master: ClientMaster;
  file: File;
  preview: ReplacementPreview;
}

const ConfigurationList = ({
  configures,
  setLoading,
  refetch,
  readOnlyMasters = false,
}: {
  configures: ClientMaster[];
  setLoading: (loading: boolean) => void;
  refetch: () => void;
  readOnlyMasters?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const { previewReplacement, replaceContent } = useMasters();
  const [pending, setPending] = useState<PendingReplacement | null>(null);
  // Who edited the file since, when a replace would overwrite their work.
  // Set alongside a live `pending`, so the override can resend the same
  // bytes with the person's answer.
  const [conflict, setConflict] = useState<ReplacementConflict | null>(null);

  const getBadgeVariant = (status: ConfigurationFileStatus | null): StatusBadgeVariant => {
    switch (status) {
      case 'COMPLETED':
        return { label: t('Configuration.Active'), variant: 'green' };
      case 'IN_PROGRESS':
        return { label: t('Configuration.InProgress'), variant: 'yellow' };
      case 'FAILED':
        return { label: t('Configuration.Failed'), variant: 'red' };
      // Two different situations, one badge. CONFIGURING is a recorded
      // state; `null` is no record at all, which a master has until the
      // sync workflow first runs over it. Both mean it is not ready.
      case 'CONFIGURING':
      case null:
        return { label: t('Configuration.Configuring'), variant: 'gray' };
      default:
        return { label: t('Configuration.Active'), variant: 'green' };
    }
  };

  const buildBadges = (value: ConfigurationFileStatus | null): JSX.Element => {
    const statusBadgeVariant = getBadgeVariant(value);
    return <Badge variant={statusBadgeVariant.variant} label={statusBadgeVariant.label} showDot />;
  };

  /**
   * Read the upload and show what it would change. Nothing is written
   * here, and the file is kept so confirming can send the same bytes --
   * the server refuses any others.
   */
  const requestPreview = useCallback(
    (input: React.ChangeEvent<HTMLInputElement>, master: ClientMaster): void => {
      const picker = input.target;
      const file = picker.files?.[0];
      if (!file) return;

      // Cleared so picking the same file again still fires a change.
      picker.value = '';

      setLoading(true);
      previewReplacement(master.id, file)
        .then((preview) => {
          setLoading(false);
          setPending({ master, file, preview });
        })
        .catch((error: Error) => {
          setLoading(false);
          sendErrorEvent(`unable to preview master replacement. ${error.message}`);
          showErrorToast(error.message);
        });
    },
    [previewReplacement, setLoading],
  );

  /**
   * Write the pending upload, optionally overwriting a newer edit.
   *
   * A conflict is not a failure here: it keeps the pending upload alive
   * and raises the dialog that names who edited the file, so the person
   * answers it rather than starting over. `force` is that answer on the
   * second pass.
   */
  const runReplace = useCallback(
    (replacement: PendingReplacement, force: boolean): void => {
      const { master, file, preview } = replacement;

      setConflict(null);
      setLoading(true);
      replaceContent(master.id, file, preview.content_hash, force)
        .then(() => {
          setLoading(false);
          setPending(null);
          refetch();
          sendCustomEvent('CONFIGURATION_FILE_REPLACED', {
            type: 'UPLOAD',
            title: master.title,
            connector: master.source,
          });
          showSuccessToast(t('Configuration.Success'));
        })
        .catch((error: Error) => {
          setLoading(false);

          if (error instanceof ReplacementConflictError) {
            // Keep `pending` so the override can resend the same bytes.
            setConflict(error.conflict);
            return;
          }

          setPending(null);
          sendErrorEvent(`unable to replace master content. ${error.message}`);
          showErrorToast(error.message);
        });
    },
    [replaceContent, refetch, setLoading, t],
  );

  const confirmReplacement = useCallback((): void => {
    if (pending) runReplace(pending, false);
  }, [pending, runReplace]);

  const overrideReplacement = useCallback((): void => {
    if (pending) runReplace(pending, true);
  }, [pending, runReplace]);

  const cancelConflict = useCallback((): void => {
    setConflict(null);
    setPending(null);
  }, []);

  const builderCell = useCallback(
    (key: string, type: string, accessor: string, master: ClientMaster): JSX.Element => {
      const value = key in master ? master[key] : '';

      if (type === 'badge') return buildBadges((value as ConfigurationFileStatus | null) ?? null);

      if (type === 'tags') {
        return (
          <div className="flex flex-wrap gap-1">
            {master.tags.map((tag) => (
              <Badge key={tag} variant="gray" label={tag} />
            ))}
          </div>
        );
      }

      // The API answers with an instant; a raw one reaches the table as
      // `2026-10-21T16:05:40.961983`.
      if (type === 'date') return <span>{formatApiDateTime(value as string, i18n.language)}</span>;

      if (type === 'label') {
        return <span className="font-semibold">{value}</span>;
      }

      if (type === 'button') {
        // On the link as well: it is recorded after the master's grants,
        // so its absence means the master is not reconciled yet and
        // nobody has been given access to the file this would replace.
        const isReplaceDisabled = !master.link || readOnlyMasters;
        return (
          <div className="flex gap-2">
            <label
              htmlFor={master.id}
              className={cn(
                'flex items-center justify-center px-3 rounded-md m-auto mr-2 text-sm font-semibold h-10 w-auto  shadow-md ',
                isReplaceDisabled
                  ? 'cursor-not-allowed bg-gray-200 ring-gray-400 ring-1 text-white'
                  : 'border-solid border-gray-300 text-gray-700 hover:border-bg-gray-300 hover:bg-gray-50 cursor-pointer hover:shadow-sm',
              )}>
              {t('Configuration.Replace')}
              <input
                id={master.id}
                type="file"
                className="hidden"
                disabled={isReplaceDisabled}
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => requestPreview(e, master)}
              />
            </label>
            <TrackedButton
              buttonName="CONFIGURATION_FILE_OPENED"
              onClick={() => openFileInNewWindow(master)}
              disabled={!master.link}
              isSubmit={false}
              className="w-[90px] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:border-none disabled:ring-0">
              <div className="flex items-center justify-center">
                <img src={open_icon} alt="icon" className="w-[15px] mr-[5px]" />
                {t('Configuration.Open')}
              </div>
            </TrackedButton>
          </div>
        );
      }
      return <div key={accessor}>{master[key]}</div>;
    },
    [t, i18n.language, readOnlyMasters, requestPreview],
  );

  // react-table rebuilds from these on every change of identity, and this
  // component re-renders through the whole approval of a replacement.
  const columns = useMemo<Column[]>(() => {
    const definitions = [
      {
        label: t('Configuration.Name'),
        accessor: 'title',
        key: 'title',
        type: 'label',
        width: 'w-[28%]',
      },
      {
        label: t('Configuration.Tags'),
        accessor: 'tags',
        key: 'tags',
        type: 'tags',
        width: 'w-[20%]',
      },
      {
        label: t('Configuration.Status'),
        accessor: 'status',
        key: 'status',
        type: 'badge',
        width: 'w-[14%]',
      },
      {
        label: t('Configuration.LastUpdated'),
        accessor: 'last_update',
        key: 'last_update',
        type: 'date',
        width: 'w-[18%]',
      },
      {
        label: '',
        type: 'button',
        accessor: 'action',
        key: 'status',
        width: 'w-[20%]',
      },
    ];

    return definitions.map((column) => ({
      Header: column.label,
      accessor: column.accessor,
      Cell: ({ row }) => builderCell(column.key, column.type, column.accessor, row.original),
      width: column.width,
    }));
  }, [t, builderCell]);

  return (
    <>
      <div className="flex mt-[10px]">
        <Table columns={columns} data={configures} pageSize={9} pagination />
      </div>

      <ReplacementPreviewModal
        isOpen={pending !== null && conflict === null}
        masterTitle={pending?.master.title ?? ''}
        fileName={pending?.file.name ?? ''}
        preview={pending?.preview ?? null}
        onCancel={() => setPending(null)}
        onConfirm={confirmReplacement}
      />

      <ConfirmationModal
        isOpen={conflict !== null}
        onClose={cancelConflict}
        onConfirm={overrideReplacement}
        buttonNamePrefix="MASTER_REPLACE_CONFLICT"
        confirmButtonText={t('Configuration.ReplaceConflictConfirm')}
        confirmText={
          conflict?.modified_by
            ? t('Configuration.ReplaceConflictBy', {
                editor: conflict.modified_by,
                when: formatApiDateTime(conflict?.modified_at, i18n.language),
              })
            : t('Configuration.ReplaceConflict', {
                when: formatApiDateTime(conflict?.modified_at, i18n.language),
              })
        }
      />
    </>
  );
};

export default ConfigurationList;
